import React from "react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

import { Variants } from "../types";
import { IconInformationCircle, IconClose } from "../Icon";

type AlertProps = {
  customClasses?: Record<string, boolean>;
  variant?: Variants;
  useIcon?: boolean;
  customIcon?: React.ReactNode;
  onClose?: () => void;
  noVariant?: boolean;
  children: React.ReactNode;
};

export const Alert = ({
  customClasses,
  variant = "info",
  useIcon = true,
  customIcon,
  onClose,
  noVariant = false,
  children,
}: AlertProps) => {
  const defaultClasses = {
    "flex items-start rounded text-sm border": true,
    "p-3": true,
  };
  const variantClasses = {
    "text-blue-700 bg-blue-100 border-blue-400": variant === "info",
    "text-green-700 bg-green-100 border-green-400": variant === "success",
    "text-yellow-700 bg-yellow-100 border-yellow-400": variant === "warning",
    "text-red-700 bg-error-100 border-red-400": variant === "error",
    "text-gray-700 bg-gray-100 border-gray-400": variant === "gray",
    "text-brand-everpine-900 bg-brand-tarnish-100 border-brand-tarnish-300":
      variant === "everpine",
  };
  const alertClasses = twMerge(
    clsx({
      ...defaultClasses,
      ...(noVariant ? {} : variantClasses),
      ...customClasses,
    }),
  );

  const closeButtonClasses = clsx({
    "rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2":
      true,
    "-my-1.5 ml-2": true,
    ...(noVariant
      ? {}
      : {
          "bg-blue-50 text-blue-500 hover:bg-blue-100 focus:ring-blue-600 focus:ring-offset-blue-50":
            variant === "info",
          "bg-green-50 text-green-500 hover:bg-green-100 focus:ring-green-600 focus:ring-offset-green-50":
            variant === "success",
          "bg-yellow-50 text-yellow-500 hover:bg-yellow-100 focus:ring-yellow-600 focus:ring-offset-yellow-50":
            variant === "warning",
          "bg-error-50 text-red-500 hover:bg-error-100 focus:ring-red-600 focus:ring-offset-error-50":
            variant === "error",
          "bg-gray-50 text-gray-500 hover:bg-gray-100 focus:ring-gray-600 focus:ring-offset-gray-50":
            variant === "gray",
          "bg-brand-tarnish-100 text-brand-everpine-700 hover:bg-brand-tarnish-300 focus:ring-brand-everpine-600 focus:ring-offset-brand-tarnish-100":
            variant === "everpine",
        }),
  });

  const AlertIcon = () =>
    customIcon || <IconInformationCircle svgClasses={{ "mr-2": true }} />;

  return (
    <div className={alertClasses} role="alert">
      {useIcon && <AlertIcon />}
      <div className={noVariant ? "min-w-0 flex-1" : "w-full"}>{children}</div>
      {onClose && (
        <button type="button" onClick={onClose} className={closeButtonClasses}>
          <span className="sr-only">Dismiss</span>
          <IconClose />
        </button>
      )}
    </div>
  );
};
